import type { DataPointSource } from "@/lib/api/building";
import { zoneKpiLabelForPersistedName } from "./zone-kpi";

export const DATA_POINT_NAME_LABEL: Record<string, string> = {
  // Leftover ids that are not KB data-point ids. Canonical KB name_fi comes from
  // GET /kb/data-point-labels or source label_fi.
  co2_ppm: "CO₂",
  ventilation_rate: "Ilmanvaihto",
  energy_use: "Energia/vyöhyke",
  lighting_level: "Valaistus",

  // Equipment measurements (legacy / BMS-specific names)
  fan_power_kw: "Puhallinteho",
  sfp_index: "SFP-luku",
  supply_temp_c: "Tulolämpötila",
  return_temp_c: "Paluulämpötila",
  co2_ppm_eq: "CO₂ (laite)",
  ventilation_rate_pct: "Ilmavirtaus",
  electricity_kwh: "Sähkö",
  heat_kwh: "Lämpö",
  peak_kw: "Huipputeho",
  cop: "COP",
  indoor_temperature_c: "Sisälämpötila",
  indoor_temperature: "Sisälämpötila",
  energy_use_kw: "Tehonkulutus",
  cooling_capacity: "Jäähdytyskapasiteetti",
  heat_recovery_efficiency: "LTO-hyötysuhde",
  supply_air_fan_speed: "Tuloilmapuhaltimen nopeus",
  supply_temperature_setpoint: "Tulolämpötilan asetusarvo",

  // Ids that still appear in the UI but are not (or no longer) KB data-point ids.
  // Canonical KB name_fi comes from GET /kb/data-point-labels / source label_fi.
  airflow: "Ilmavirta",
  speed_pct: "Pyörimisnopeus",
  duct_pressure: "Kanavapaine",
  duct_pressure_setpoint: "Kanavapaineenasetus",
  zone_co2: "CO₂ (vyöhyke)",
  supply_filter_dp: "Tulosuodattimen paine-ero",
  extract_filter_dp: "Poistosuodattimen paine-ero",
  heat_recovery_supply_temp: "LTO:n jälkeinen tulolämpötila",
  heat_recovery_exhaust_temp: "LTO:n jälkeinen poistolämpötila",
  supply_temperature_boiler: "Menolämpötila (kattila)",
  cold_start_count: "Kylmäkäynnistykset",

  // Weather & external prices
  outdoor_temperature_c: "Ulkolämpötila",
  solar_radiation_wm2: "Auringonsäteily",
  electricity_price_eur_kwh: "Sähkön hinta",

  // Retired optimization ports — historical-only. The AHU's live recipe ports became
  // offset-native on 2026-09-08 (shared-sensors v8/v9): `supply_air_temp_override` was replaced
  // by `supply_air_temp_offset` and `supply_fan_speed_override` by `ventilation_level_offset`.
  // The old rows are absolute values, not offsets, so they were never renamed in
  // data_point_history — reading them as offsets would change what they mean. They still need a
  // Finnish label to render.
  supply_air_temp_override: "Tuloilman lämpötilan pakko-ohjaus",
  supply_fan_speed_override: "Tulopuhaltimen nopeuden pakko-ohjaus",

  // Analytics findings — historical-only. The narrator agent that wrote these
  // (`analysis_summary`, `analysis_finding_count`, `analysis_finding_{finding_type}_count`) was
  // removed in story #1366; Datavahtimestari is now the deterministic KB data-quality pass
  // itself and writes no data points of its own. Existing rows stay in data_point_history and
  // still need a Finnish label to render.
  analysis_summary: "Analyysikooste",
  analysis_finding_count: "Havainnot",
  // No per-type rows here — `finding_type` was a KB quality-check id, so a curated row per type
  // would have gone stale the moment a sensor file gained a check. `dataPointLabel` falls back
  // to the de-underscored name, which stays truthful without tracking the KB.

  // Simulation scenarios
  sim_total_eur_month: "Simuloitu potentiaali",
  sim_co2_demand_ventilation_eur_month: "Tarpeenmukainen ilmanvaihto (CO₂)",
  sim_night_setback_eur_month: "Yöaikainen lämpötilan alennus",
  sim_electricity_price_optimization_eur_month: "Sähkön hintaoptimointi",
  sim_building_usage_setback_eur_month: "Käyttöaikainen optimointi",
  sim_weather_preconditioning_eur_month: "Sääennusteoptimointi",
  sim_demand_response_eur_month: "Kuormanjousto",
};

// A quantity-family derived estimator's minted sibling id follows the documented
// `<logical_quantity>__<estimator_id>` convention (docs/spec/concepts.md §
// Quantity-family sibling channels), e.g. `supply_air_flow__from_dp`. Neither half is a curated
// or KB-supplied label on its own, so the raw id would otherwise fall through to the ugly
// de-underscored fallback below. Not a per-signal hardcode: this derives the label from the
// BASE quantity's own existing label (curated or API-provided) plus a generic "(laskettu)"
// suffix — the same word `DATA_POINT_NATURE_LABEL.calculated` already uses for a
// `nature: "calculated"` reading, so the wording stays consistent system-wide.
const QUANTITY_FAMILY_SIBLING_ID = /^(.+)__[a-z0-9_]+$/;

export function dataPointLabel(
  name: string,
  extra?: Record<string, string>,
): string {
  // Non-KB leftovers in the map; everything else uses extra (KB name_fi).
  const curated = DATA_POINT_NAME_LABEL[name] ?? extra?.[name];
  if (curated) return curated;

  // Persisted zone-KPI series (`kb_zone_kpi_*`) are not zone.yaml data_points, so they never
  // get a KB name_fi. Reuse the same Finnish titles Sisäilmaolosuhteet already shows.
  const zoneKpiLabel = zoneKpiLabelForPersistedName(name);
  if (zoneKpiLabel) return zoneKpiLabel;

  const siblingMatch = name.match(QUANTITY_FAMILY_SIBLING_ID);
  if (siblingMatch) {
    const baseName = siblingMatch[1];
    const baseLabel = dataPointLabel(baseName, extra);
    return `${baseLabel} (laskettu)`;
  }

  return name.replace(/_/g, " ");
}

export function labelsFromOneSource(
  source: Pick<DataPointSource, "available_names">,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const meta of source.available_names) {
    if (meta.label_fi) map[meta.name] = meta.label_fi;
  }
  return map;
}

export function labelsFromSources(
  sources: DataPointSource[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const source of sources) {
    Object.assign(map, labelsFromOneSource(source));
  }
  return map;
}

export function labelsBySourceId(
  sources: DataPointSource[],
): Map<string, Record<string, string>> {
  const map = new Map<string, Record<string, string>>();
  for (const source of sources) {
    map.set(source.source_id, labelsFromOneSource(source));
  }
  return map;
}

// KB `unit` on available_names — same per-source map as labels. Signaalit already
// formats with this; zone/equipment cards were reading history `sample.unit` only,
// which is often null after CSV ingest.
export function unitsFromOneSource(
  source: Pick<DataPointSource, "available_names">,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const meta of source.available_names) {
    if (meta.unit) map[meta.name] = meta.unit;
  }
  return map;
}

export function unitsFromSources(
  sources: DataPointSource[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const source of sources) {
    Object.assign(map, unitsFromOneSource(source));
  }
  return map;
}

export function unitsBySourceId(
  sources: DataPointSource[],
): Map<string, Record<string, string>> {
  const map = new Map<string, Record<string, string>>();
  for (const source of sources) {
    map.set(source.source_id, unitsFromOneSource(source));
  }
  return map;
}
